import React, { useState } from "react";
import {
  StyledAppBar,
  StyledToolbar,
  ActionButtons,
  StyledIconButton,
  LogoContainer,
  BrandText,
  StyledMenu,
  MenuHeader,
  MenuTitle,
  NotificationList,
  NotificationListItem,
  NotificationMessage,
  NotificationTime,
  NotificationLink,
  ReadMoreButton,
  NotificationBadge,
} from "./Topbar.styled";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../hooks/useNotification";
import { StorageHelper } from "../../utils/storageHelper";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { formatDistanceToNow } from "date-fns";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import useSound from "use-sound";

const Topbar = ({ sidebarCollapsed, userDetails }) => {
  const navigate = useNavigate();
  const {
    useNotify,
    notificationsData,
    notificationsLoading,
    markAsRead,
    cacheOperations,
  } = useNotification();
  const { data: notify, loading: notiLoading } = useNotify;
  const [notification, setNotification] = React.useState([]);
  const [readNotifications, setReadNotifications] = React.useState(new Set());
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedMessages, setExpandedMessages] = useState(new Set());
  const [play, { stop }] = useSound("/sounds/multi-pop-2-188167.mp3", {
    volume: 0.5,
  });

  React.useEffect(() => {
    if (
      !notificationsLoading &&
      notificationsData?.GetNotificationsByResourceById?.notifications
    ) {
      const notifications =
        notificationsData.GetNotificationsByResourceById.notifications;
      setNotification(notifications);
      const readIds = notifications
        .filter((notif) => notif.is_read)
        .map((notif) => notif.notification_id);
      setReadNotifications(new Set(readIds));
    }
  }, [notificationsData, notificationsLoading]);

  React.useEffect(() => {
    if (
      !notiLoading &&
      StorageHelper.get("userDetails")?.resource_id.toString() ===
        notify?.PushNotification?.resource_id
    ) {
      const newNotification = notify?.PushNotification;
      if (newNotification) {
        play();
        console.log(newNotification);
        const resourceId = parseInt(
          StorageHelper.get("userDetails")?.resource_id.toString()
        );
        const currentNotifications =
          cacheOperations.readNotifications(resourceId);
        const exists = currentNotifications.some(
          (n) => n.notification_id === newNotification.notification_id
        );

        if (!exists) {
          const updatedNotifications = [
            newNotification,
            ...currentNotifications,
          ];
          cacheOperations.writeNotifications(updatedNotifications, resourceId);
          setNotification(updatedNotifications);
          if (newNotification.is_read) {
            setReadNotifications(
              (prev) => new Set([...prev, newNotification.notification_id])
            );
          }
        }
      }
    }
  }, [notify, notiLoading, cacheOperations]);

  const handleNotificationClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const markNotificationAsRead = async (notificationId) => {
    if (!readNotifications.has(notificationId)) {
      try {
        await markAsRead([notificationId]);
        setReadNotifications((prev) => new Set([...prev, notificationId]));
      } catch (error) {
        console.error("Error marking notification as read:", error);
      }
    }
  };

  const handleNotificationAction = async (notification) => {
    await markNotificationAsRead(notification.notification_id);
    if (notification.action_link) {
      handleClose();
      const url = new URL(notification.action_link);
      const path = url.pathname;
      navigate(path);
    }
  };

  const toggleMessageExpansion = async (notificationId, event) => {
    event.stopPropagation();
    await markNotificationAsRead(notificationId);
    setExpandedMessages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  };

  const isMessageLong = (message) => message.length > 100;

  const formatNotificationDate = (timestamp) => {
    try {
      if (!timestamp) return "Just now";

      let date;
      if (!isNaN(timestamp)) {
        const timestampNum = Number(timestamp);
        date = new Date(
          timestampNum.toString().length > 10
            ? timestampNum
            : timestampNum * 1000
        );
      } else {
        date = new Date(timestamp);
      }

      if (isNaN(date.getTime())) return "Just now";

      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins} minutes ago`;
      if (diffHours < 24) return `${diffHours} hours ago`;
      if (diffDays === 1) return "Yesterday";
      if (diffDays < 7) return `${diffDays} days ago`;

      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Just now";
    }
  };

  const unreadCount = notification.filter(
    (notif) => !notif.is_read && !readNotifications.has(notif.notification_id)
  ).length;

  return (
    <StyledAppBar position="fixed">
      <StyledToolbar>
        <LogoContainer>
          <div className="logo-wrapper">
            <img src="/assets/solutionec_logo.png" alt="Solutionec Logo" />
            <img src="/assets/applogo.svg" alt="PCRM Logo" style={{height:"35%"}} />
          </div>
        </LogoContainer>
        <ActionButtons>
          <StyledIconButton onClick={handleNotificationClick}>
            {unreadCount > 0 ? (
              <NotificationBadge
                badgeContent={unreadCount}
                max={99}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <NotificationsActiveIcon sx={{ color: "white" }} />
              </NotificationBadge>
            ) : (
              <NotificationsActiveIcon sx={{ color: "white" }} />
            )}
          </StyledIconButton>
        </ActionButtons>
      </StyledToolbar>

      <StyledMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuHeader>
          <MenuTitle>
            Notifications {unreadCount > 0 && `(${unreadCount} new)`}
          </MenuTitle>
        </MenuHeader>

        <NotificationList>
          {notification.length === 0 ? (
            <MenuItem>
              <Typography variant="body2">No notifications</Typography>
            </MenuItem>
          ) : (
            notification.map((notif) => {
              const isUnread =
                !notif.is_read && !readNotifications.has(notif.notification_id);
              return (
                <NotificationListItem
                  key={notif.notification_id}
                  onClick={() => handleNotificationAction(notif)}
                  isUnread={isUnread}
                >
                  <Box sx={{ width: "100%" }}>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                    >
                      {isUnread && (
                        <FiberManualRecordIcon
                          sx={{
                            fontSize: 10,
                            color: "error.main",
                            mt: 0.5,
                            animation: "pulse 2s infinite",
                            "@keyframes pulse": {
                              "0%": {
                                opacity: 1,
                              },
                              "50%": {
                                opacity: 0.5,
                              },
                              "100%": {
                                opacity: 1,
                              },
                            },
                          }}
                        />
                      )}
                      <NotificationMessage
                        sx={{
                          maxHeight: expandedMessages.has(notif.notification_id)
                            ? "none"
                            : "2.6em",
                          WebkitLineClamp: expandedMessages.has(
                            notif.notification_id
                          )
                            ? "unset"
                            : 2,
                          fontWeight: isUnread ? 600 : 400,
                          color: isUnread ? "text.primary" : "text.secondary",
                        }}
                      >
                        {notif.message}
                      </NotificationMessage>
                    </Box>
                    {isMessageLong(notif.message) && (
                      <ReadMoreButton
                        onClick={(e) =>
                          toggleMessageExpansion(notif.notification_id, e)
                        }
                      >
                        {expandedMessages.has(notif.notification_id)
                          ? "Read less"
                          : "Read more"}
                      </ReadMoreButton>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mt: 0.5,
                      }}
                    >
                      <NotificationTime>
                        <AccessTimeIcon />
                        {formatNotificationDate(notif.created_on)}
                      </NotificationTime>
                      {notif.action_link && (
                        <NotificationLink>
                          View
                          <ChevronRightIcon />
                        </NotificationLink>
                      )}
                    </Box>
                  </Box>
                </NotificationListItem>
              );
            })
          )}
        </NotificationList>
      </StyledMenu>
    </StyledAppBar>
  );
};

export default Topbar;
